import { useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Copy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const Option = ({ href, label, hint, testId }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
    className="block rounded-xl border border-slate-200 px-4 py-3 transition-colors hover:border-indigo-300 hover:bg-indigo-50">
    <p className="font-semibold">{label}</p>
    <p className="text-xs text-slate-500">{hint}</p>
  </a>
);

export const CalendarSync = () => {
  const [path, setPath] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    setOpen(true);
    try {
      setPath((await api.get("/me/calendar")).data.path);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reset = async () => {
    if (!window.confirm("Make a new private link? Calendars using the old link will stop updating until you add the new one.")) return;
    try {
      setPath((await api.post("/me/calendar/reset")).data.path);
      toast.success("New calendar link created");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const url = path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : "";
  const webcal = url.replace(/^https?:/, "webcal:");
  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={load} data-testid="calendar-sync-btn"><CalendarPlus className="mr-1 h-4 w-4" />Add to my calendar</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="calendar-sync-dialog">
          <DialogHeader><DialogTitle>Add your shifts to your calendar</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Your published shifts appear in your own calendar and update automatically when the rota changes.</p>
          {path && (
            <div className="space-y-2">
              <Option href={`https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal)}`} label="Google Calendar" hint="Google refreshes subscribed calendars every few hours" testId="calendar-google-link" />
              <Option href={webcal} label="Apple Calendar (iPhone, iPad, Mac)" hint="Tap Subscribe when asked" testId="calendar-apple-link" />
              <Option href={`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=${encodeURIComponent("My shifts")}`} label="Outlook" hint="Outlook.com or Microsoft 365" testId="calendar-outlook-link" />
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"))} data-testid="calendar-copy-btn"><Copy className="mr-1 h-4 w-4" />Copy link</Button>
                <Button variant="ghost" size="sm" onClick={reset} data-testid="calendar-reset-btn"><RefreshCw className="mr-1 h-4 w-4" />New link</Button>
              </div>
              <p className="text-xs text-slate-500">Keep this link private. Anyone with it can see your shift times.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
